import React, { forwardRef } from 'react';

import { MenuButton, MenuItem } from '@chakra-ui/react';

import { ExternalButton } from '@components/atoms/nav/ExternalButton';
import { InternalButton } from '@components/atoms/nav/InternalButton';

import { AsButton } from '@components/atoms/AsButton';
import { AsLink } from '@components/atoms/AsLink';

// Root button at top of menu
export const RootMenuItem = forwardRef(
  (
    {
      isOpen,
      menuItemData,
      children,
      containText = false,
      onOpen = () => {},
      onClose = () => {},
      onHandleNavigate = () => {}
    },
    ref
  ) => {
    const renderAsButton = menuItemData.attributes.children.data.length > 0;
    const navProps =
      renderAsButton === true
        ? { isOpen }
        : {
            url: menuItemData.attributes.url,
            title: menuItemData.attributes.title,
            target: menuItemData.attributes.target
          };

    return (
      <MenuButton
        role="menuitem"
        as={renderAsButton === true ? AsButton : AsLink}
        url={renderAsButton === true ? null : menuItemData.attributes.url}
        target={renderAsButton === true ? null : menuItemData.attributes.target}
        color={isOpen ? `secondary.pink` : `primary.dark-blue`}
        fontSize={{
          base: `sm!important`,
          xl: containText === true ? `sm!important` : `md!important`
        }}
        pr={{ base: `0!important`, xl: `1.125rem!important` }}
        pl={{ base: `5!important`, xl: `0!important` }}
        _hover={{ color: `secondary.pink` }}
        onClick={() => {
          if (menuItemData.attributes.children.data.length > 0) {
            return isOpen ? onClose() : onOpen();
          }
          return onHandleNavigate(
            menuItemData.attributes.url,
            menuItemData.attributes.title
          );
        }}
        {...navProps}
      >
        <span ref={ref}>{children}</span>
      </MenuButton>
    );
  }
);

// Collection item that appears at first menu layer
export const CollectionMenuItem = ({
  url,
  target,
  isOpen,
  index,
  children,
  onClick = () => {}
}) => {
  return (
    <MenuItem
      as={AsLink}
      url={url}
      target={target}
      role="presentation"
      bg="none"
      px="1.2rem"
      py="1"
      h="auto"
      transition={`opacity 0.05s ease-in ${index * 0.05}s`}
      fontSize={[`sm`, undefined, `md`]}
      fontFamily="body"
      fontWeight="800"
      opacity={isOpen ? `1` : `0`}
      borderRadius="xs"
      border="2px solid transparent"
      color="primary.dark-blue"
      textDecoration="underline"
      textDecorationColor="transparent"
      _hover={{
        bg: `none`,
        border: `2px solid transparent`,
        textDecoration: `underline`,
        color: `secondary.pink`
      }}
      onClick={onClick}
    >
      {children}
    </MenuItem>
  );
};

// Item that appears at the foot of each menu layer
export const EndMenuItem = ({
  mainButtonIsExternal,
  menuItemData,
  isOpen,
  handleCTAFocused = () => {},
  handleCTABlur = () => {},
  onInternalButtonClicked = () => {}
}) => {
  return (
    <MenuItem
      as="div"
      role="presentation"
      cursor="default"
      _hover={{
        background: `transparent`
      }}
      pointerEvents="none"
      onClick={
        () => {
          if (mainButtonIsExternal === true) {
            return;
          }

          onInternalButtonClicked(
            menuItemData.attributes.url,
            menuItemData.attributes.title,
            2
          );
        }
        // eslint-disable-next-line react/jsx-curly-newline
      }
      onFocus={handleCTAFocused}
      onBlur={handleCTABlur}
    >
      {mainButtonIsExternal === true ? (
        <ExternalButton
          url={menuItemData.attributes.url}
          target={menuItemData.attributes.target}
          title={menuItemData.attributes.title}
          descendants={menuItemData.attributes.children.data}
          isOpen={isOpen}
        />
      ) : (
        <InternalButton
          title={menuItemData.attributes.title}
          descendants={menuItemData.attributes.children.data}
          isOpen={isOpen}
          onClick={
            () =>
              onInternalButtonClicked(
                menuItemData.attributes.url,
                menuItemData.attributes.title,
                2,
                true
              )
            // eslint-disable-next-line react/jsx-curly-newline
          }
        />
      )}
    </MenuItem>
  );
};
